import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasFunctionAccess } from '../permissionConfig';
import { API_BASE } from '../config';
import NotificationCenter from './NotificationCenter';

const DISMISS_KEY = 'sf_alerts_dismissed'; // sessionStorage — clears on logout/reload

/* ---------------------------------------------------------------------- */
/* Icons                                                                   */
/* ---------------------------------------------------------------------- */
const IconCheck = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);
const IconWarning = (p) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconBell = (p) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/* ---------------------------------------------------------------------- */
/* Dismiss tracking — sessionStorage snapshot of item IDs already read.    */
/* An alert stays hidden only while its current items are a subset of     */
/* what was dismissed; any genuinely new item makes it reappear.          */
/* ---------------------------------------------------------------------- */
function getDismissed() {
    try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
}
function saveDismissed(map) {
    sessionStorage.setItem(DISMISS_KEY, JSON.stringify(map));
}
function isFullyDismissed(alert, dismissedMap) {
    const dismissedIds = dismissedMap[alert.id];
    if (!dismissedIds) return false;
    return alert.itemIds.every(id => dismissedIds.includes(id));
}

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const then = new Date(dateStr);
    if (isNaN(then)) return '';
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

/* ---------------------------------------------------------------------- */
/* Component                                                               */
/* ---------------------------------------------------------------------- */
export default function ManagerAlerts({ user, maxHeight }) {
    const navigate = useNavigate();
    const [rawAlerts, setRawAlerts] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [dismissed, setDismissedState] = useState(getDismissed());

    const canSeeSO  = hasFunctionAccess(user, 'po_so_lifecycle', 'so_kam_review');
    const canSeeDMS = hasFunctionAccess(user, 'dms', 'uprising_inventory');

    const loadAlerts = useCallback(async () => {
        setLoading(true);
        const built = [];

        try {
            if (canSeeSO) {
                const identityQS = `&username=${encodeURIComponent(user?.username || '')}&role=${encodeURIComponent(user?.role || '')}`;
                const res  = await fetch(`${API_BASE}/so-kam-review?action=pending&kam=${identityQS}`);
                const list = await res.json();
                if (Array.isArray(list) && list.length > 0) {
                    const itemIds = list.map(so => so.voucher_no);
                    const latest  = list.reduce((max, so) => (!max || (so.so_date > max) ? so.so_date : max), null);
                    built.push({
                        id: 'so-review',
                        tag: 'PENDING REVIEW',
                        tint: '#F7E5EA', color: '#B05770', Icon: IconCheck,
                        title: `${list.length} new Sales Order${list.length !== 1 ? 's' : ''} for reviewing`,
                        time: timeAgo(latest),
                        action: 'Review',
                        onAction: () => navigate('/po-so-lifecycle/so-kam-review'),
                        itemIds,
                    });
                }
            }
        } catch (e) { console.error('Failed to load SO alert', e); }

        try {
            if (canSeeDMS) {
                const res  = await fetch(`${API_BASE}/dms?action=projection&distributor=${encodeURIComponent('UPRISING DISTRIBUTION CO. GHZ.')}`);
                const data = await res.json();
                const rows = data?.rows || [];
                const outOfStock = rows.filter(r => r.status === 'OUT');
                const critical    = rows.filter(r => r.status === 'CRITICAL');

                if (outOfStock.length > 0) {
                    built.push({
                        id: 'uprising-oos',
                        tag: 'UPRISING CO. \u00b7 OUT OF STOCK',
                        tint: '#F7E5EA', color: '#B05770', Icon: IconWarning,
                        title: outOfStock.length === 1
                            ? `1 SKU out of stock \u2014 ${outOfStock[0].item_name}`
                            : `${outOfStock.length} SKUs out of stock`,
                        time: timeAgo(data.stock_date),
                        action: 'Review',
                        onAction: () => navigate('/dms'),
                        itemIds: outOfStock.map(r => r.ean || r.item_name),
                    });
                }
                if (critical.length > 0) {
                    built.push({
                        id: 'uprising-critical',
                        tag: 'UPRISING CO. \u00b7 BELOW CRITICAL',
                        tint: '#F5EDDC', color: '#9A7B3E', Icon: IconWarning,
                        title: `${critical.length} SKU${critical.length !== 1 ? 's' : ''} below critical stock level`,
                        time: timeAgo(data.stock_date),
                        action: 'Review',
                        onAction: () => navigate('/dms'),
                        itemIds: critical.map(r => r.ean || r.item_name),
                    });
                }
            }
        } catch (e) { console.error('Failed to load DMS alerts', e); }

        setRawAlerts(built);
        setLoading(false);
    }, [canSeeSO, canSeeDMS, navigate]);

    useEffect(() => { loadAlerts(); /* eslint-disable-next-line */ }, []);

    const visibleAlerts = rawAlerts.filter(a => !isFullyDismissed(a, dismissed));

    function markAllRead() {
        const updated = { ...dismissed };
        visibleAlerts.forEach(a => { updated[a.id] = a.itemIds; });
        saveDismissed(updated);
        setDismissedState(updated);
    }

    if (!user?.username) return null;

    return (
        <aside style={maxHeight ? { ...S.aside, maxHeight, overflow: 'hidden' } : S.aside}>
            {(canSeeSO || canSeeDMS) && (
                <>
                    <div style={S.header}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={S.title}>Manager Alerts</span>
                            {visibleAlerts.length > 0 && <span style={S.countPill}>{visibleAlerts.length} new</span>}
                        </div>
                        {visibleAlerts.length > 0 && (
                            <span style={S.markAll} onClick={markAllRead}>Mark all read</span>
                        )}
                    </div>

                    {loading ? (
                        <div style={S.emptyWrap}>
                            <div style={S.emptyText}>Loading...</div>
                        </div>
                    ) : visibleAlerts.length === 0 ? (
                        <div style={S.emptyWrap}>
                            <div style={S.emptyIcon}><IconBell /></div>
                            <div style={S.emptyTitle}>No alerts yet</div>
                            <div style={S.emptyText}>Anything that needs your attention will show up here.</div>
                        </div>
                    ) : (
                        <div style={S.list}>
                            {visibleAlerts.map(a => (
                                <div key={a.id} style={S.card}>
                                    <div style={{ ...S.iconBadge, background: a.tint, color: a.color }}>
                                        <a.Icon />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ ...S.tag, color: a.color }}>{a.tag}</span>
                                        <div style={S.alertTitle}>{a.title}</div>
                                    </div>
                                    <div style={S.footer}>
                                        <button style={S.actionBtn} onClick={a.onAction}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#F6F2FB')}
                                            onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                                            {a.action} &rarr;
                                        </button>
                                        <span style={S.time}>{a.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <hr style={S.rule} />
                </>
            )}

            {/* Live "wall of latest updates" — every notifyEvent() the user has view
                or edit access to, persisted server-side (manager-alerts.js). This is
                the same feed the top-nav/sidebar bell shows, surfaced inline here too
                since that's where users actually look for "Manager Alerts". */}
            <NotificationCenter user={user} variant="inline" label="Recent Activity" />
        </aside>
    );
}

const S = {
    aside: { minHeight: 200, borderRadius: 16, background: '#fff', border: '1px solid #ECE7F2', padding: 22, display: 'flex', flexDirection: 'column', fontFamily: "'Poppins', sans-serif" },
    header: { flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
    title: { fontSize: 19, fontWeight: 500, color: '#231C30' },
    countPill: { fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: '#F6E3E8', color: '#C25E77' },
    markAll: { fontSize: 12.5, color: '#8C6FC0', fontWeight: 500, cursor: 'pointer' },
    rule: { border: 'none', borderTop: '1px solid #F0ECF5', margin: '18px 0' },
    list: { flex: 'none', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 },
    card: { borderRadius: 12, background: '#FBFAFD', border: '1px solid #F0ECF5', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 },
    iconBadge: { width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
    tag: { fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em' },
    alertTitle: { fontSize: 14, fontWeight: 700, color: '#231C30', lineHeight: 1.35, marginTop: 3 },
    footer: { flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 6 },
    actionBtn: { padding: '8px 13px', borderRadius: 8, border: '1px solid #E4DEEE', background: '#fff', color: '#6E58A6', fontFamily: 'inherit', fontWeight: 500, fontSize: 11.5, cursor: 'pointer' },
    time: { fontSize: 10, color: '#A79EB6' },
    emptyWrap: { flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 6, padding: 20 },
    emptyIcon: { width: 52, height: 52, borderRadius: 14, background: '#F5F2FA', color: '#8C6FC0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
    emptyTitle: { fontSize: 15, fontWeight: 500, color: '#231C30' },
    emptyText: { fontSize: 13, color: '#9A90AE', fontWeight: 300, maxWidth: 220, lineHeight: 1.5 },
};